'use client';

import { useState, useEffect } from 'react';

import Sidebar from '@/components/common/Sidebar';
import Icon from '@/components/ui/AppIcon';

interface SettingsSection {
  id: string;
  title: string;
  description: string;
  icon: string;
  items: SettingItem[];
}

interface SettingItem {
  id: string;
  label: string;
  value: string | boolean;
  type: 'text' | 'toggle' | 'select';
  options?: string[];
}

export default function SettingsPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState<SettingsSection[]>([]);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setSettings([
        {
          id: 'profile',
          title: 'Profile Settings',
          description: 'Manage your account information and preferences',
          icon: 'UserCircleIcon',
          items: [
            {
              id: 'name',
              label: 'Full Name',
              value: 'Caregiver Name',
              type: 'text'
            },
            {
              id: 'email',
              label: 'Email Address',
              value: 'caregiver@example.com',
              type: 'text'
            },
            {
              id: 'phone',
              label: 'Phone Number',
              value: '+1 (555) 123-4567',
              type: 'text'
            }
          ]
        },
        {
          id: 'notifications',
          title: 'Notification Preferences',
          description: 'Control how and when you receive alerts',
          icon: 'BellIcon',
          items: [
            {
              id: 'email-notifications',
              label: 'Email Notifications',
              value: true,
              type: 'toggle'
            },
            {
              id: 'sms-notifications',
              label: 'SMS Notifications',
              value: true,
              type: 'toggle'
            },
            {
              id: 'push-notifications',
              label: 'Push Notifications',
              value: false,
              type: 'toggle'
            },
            {
              id: 'crisis-alerts',
              label: 'Crisis Alerts',
              value: true,
              type: 'toggle'
            }
          ]
        },
        {
          id: 'care',
          title: 'Care Settings',
          description: 'Configure care monitoring and check-in preferences',
          icon: 'HeartIcon',
          items: [
            {
              id: 'check-in-frequency',
              label: 'Check-In Frequency',
              value: 'Daily',
              type: 'select',
              options: ['Daily', 'Every 2 Days', 'Weekly']
            },
            {
              id: 'check-in-time',
              label: 'Preferred Check-In Time',
              value: '9:00 AM',
              type: 'select',
              options: ['9:00 AM', '12:00 PM', '3:00 PM', '6:00 PM']
            },
            {
              id: 'medication-reminders',
              label: 'Medication Reminders',
              value: true,
              type: 'toggle'
            }
          ]
        },
        {
          id: 'privacy',
          title: 'Privacy & Security',
          description: 'Manage your data and security preferences',
          icon: 'LockClosedIcon',
          items: [
            {
              id: 'two-factor',
              label: 'Two-Factor Authentication',
              value: false,
              type: 'toggle'
            },
            {
              id: 'data-sharing',
              label: 'Share Data with Family',
              value: true,
              type: 'toggle'
            }
          ]
        }
      ]);
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleToggle = (sectionId: string, itemId: string) => {
    setSettings(prevSettings =>
      prevSettings.map(section =>
        section.id === sectionId
          ? {
              ...section,
              items: section.items.map(item =>
                item.id === itemId && item.type === 'toggle'
                  ? { ...item, value: !item.value }
                  : item
              )
            }
          : section
      )
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <main className="pt-20 pb-[88px] lg:pb-8 max-w-[960px] mx-auto px-6 w-full">
        <div className="py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
              Settings
            </h1>
            <p className="text-muted-foreground">
              Manage your profile, notifications, and care preferences
            </p>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
              <p className="text-muted-foreground">Loading settings...</p>
            </div>
          ) : (
            <div className="space-y-6 max-w-4xl">
              {settings.map((section) => (
                <div
                  key={section.id}
                  className="glass overflow-hidden"
                >
                  <div className="p-6 border-b border-sage-200/30">
                    <div className="flex items-center gap-3 mb-2">
                      <Icon
                        name={section.icon as any}
                        size={24}
                        className="text-primary"
                      />
                      <h2 className="text-xl font-heading font-semibold text-foreground">
                        {section.title}
                      </h2>
                    </div>
                    <p className="text-muted-foreground">{section.description}</p>
                  </div>
                  <div className="divide-y divide-sage-200/30">
                    {section.items.map((item) => (
                      <div
                        key={item.id}
                        className="p-6 flex items-center justify-between hover:bg-muted/50 transition-smooth"
                      >
                        <div className="flex-1">
                          <label
                            htmlFor={item.id}
                            className="font-caption font-medium text-foreground"
                          >
                            {item.label}
                          </label>
                        </div>
                        <div>
                          {item.type === 'toggle' && (
                            <button
                              id={item.id}
                              onClick={() => handleToggle(section.id, item.id)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                item.value ? 'bg-primary' : 'bg-gray-300'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  item.value ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          )}
                          {item.type === 'text' && (
                            <input
                              id={item.id}
                              type="text"
                              value={item.value as string}
                              readOnly
                              className="px-3 py-2 border border-sage-200/30 rounded-lg bg-background text-foreground"
                            />
                          )}
                          {item.type === 'select' && (
                            <select
                              id={item.id}
                              value={item.value as string}
                              className="px-3 py-2 border border-sage-200/30 rounded-lg bg-background text-foreground"
                            >
                              {item.options?.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div className="glass border-red-200/50 p-6">
                <h3 className="text-lg font-heading font-semibold text-red-600 mb-2">
                  Danger Zone
                </h3>
                <p className="text-muted-foreground mb-4">
                  Irreversible actions that affect your account
                </p>
                <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-smooth">
                  Delete Account
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}